import { useEffect, useMemo, useRef, useState } from 'react';
import type { MailMessage } from '../../../lib/stores/mail/types';
import { parseSignature } from '../../../lib/mail/signatureParser';
import {
  mergeCompanyDraft, draftToValues, collectThreadParticipants,
  type CompanyDraft, type ExistingMatches,
} from '../../../lib/mail/companyProspect';
import { findExistingEntities } from '../../../lib/mail/prospectLookup';
import { fetchProspectPreview } from '../../../lib/mail/prospectPreviewClient';
import { extractCompanyDomain } from '../../../lib/utils/companyDomainMatch';
import type { CreateKind, PersonValues } from '../../../lib/mail/createFromProspect';

interface Args {
  isOpen: boolean;
  active: boolean;          // il tab "Crea nuovo" e` quello visibile
  threadKey: string;
  messages: MailMessage[];
  fromEmail: string | null;
  fromName: string | null;
  organizationId: string;
}

/**
 * Raccoglie i dati del prospect: firma (locale e istantanea), poi sito e VIES
 * tramite flusia-core, con controllo anti-doppione. I campi modificati a mano
 * dall'utente non vengono mai sovrascritti dalle fonti automatiche.
 */
export function useProspectDraft({ isOpen, active, threadKey, messages, fromEmail, fromName, organizationId }: Args) {
  const [kind, setKind] = useState<CreateKind>('person');
  const [siteCandidates, setSiteCandidates] = useState<string[]>([]);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [socialLinks, setSocialLinks] = useState<string[]>([]);
  const [companyValues, setCompanyValues] = useState<Record<string, string>>({});
  const [companyDraft, setCompanyDraft] = useState<CompanyDraft>({});
  const [person, setPerson] = useState<PersonValues>({ business_name: '', email: '', phone: '', contact_role: '' });
  const [analyzing, setAnalyzing] = useState(false);
  const [matches, setMatches] = useState<ExistingMatches | null>(null);
  const [extraPeople, setExtraPeople] = useState<Set<string>>(new Set());

  const touchedRef = useRef<Set<string>>(new Set());
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // 1. Analisi locale della firma: nessuna chiamata di rete.
  useEffect(() => {
    if (!isOpen) return;
    touchedRef.current = new Set();
    setExtraPeople(new Set());

    const inbound = messagesRef.current.find(m => !(m.label_ids || []).includes('SENT'))
      || messagesRef.current[0];
    const sig = parseSignature({ bodyHtml: inbound?.body_html, bodyText: inbound?.body_text });
    const domain = extractCompanyDomain(fromEmail);

    const candidates = [...new Set([...sig.websites, ...(domain ? [domain] : [])])];
    setSiteCandidates(candidates);
    setSocialLinks(sig.socialLinks);
    setSelectedSite(candidates.length === 1 ? candidates[0] : (domain || null));
    setKind(domain ? 'both' : 'person');
    setPerson({
      business_name: fromName || '',
      email: fromEmail || '',
      // Alla persona il cellulare, se c'e': il fisso in firma e` il centralino aziendale.
      phone: sig.mobiles[0] || sig.phones[0] || '',
      contact_role: sig.role || '',
    });

    const draft = mergeCompanyDraft({ signature: sig, domain });
    setCompanyDraft(draft);
    setCompanyValues(draftToValues(draft));
  }, [isOpen, threadKey, fromEmail, fromName]);

  // 2. Sito e VIES tramite core, solo quando serve davvero.
  useEffect(() => {
    if (!isOpen || !active || kind === 'person' || !selectedSite) return;

    const controller = new AbortController();
    setAnalyzing(true);

    (async () => {
      try {
        const known = companyValues.vat_number || null;
        const preview = await fetchProspectPreview({
          websites: [selectedSite], vatNumber: known, signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        // Se il sito ha rivelato una partita IVA che non avevamo, il VIES vale la seconda chiamata.
        let vies = preview.vies;
        const scrapedVat = (preview.website as Record<string, unknown> | null)?.vat_number as string | undefined;
        if (!vies && scrapedVat && scrapedVat !== known) {
          const second = await fetchProspectPreview({
            websites: [], vatNumber: scrapedVat, signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          vies = second.vies;
        }

        const merged = mergeCompanyDraft(
          { website: preview.website, vies, domain: selectedSite },
          touchedRef.current,
        );
        setCompanyDraft(prev => ({ ...merged, ...prev }));
        setCompanyValues(prev => {
          const next = { ...draftToValues(merged) };
          // I valori gia` presenti (firma o digitati) restano al loro posto.
          for (const [k, v] of Object.entries(prev)) if (v) next[k] = v;
          return next;
        });
      } finally {
        if (!controller.signal.aborted) setAnalyzing(false);
      }
    })();

    return () => { controller.abort(); };
    // companyValues.vat_number e' letto ma volutamente fuori dalle dipendenze: lo scraping
    // deve ripartire solo al cambio di sito, non a ogni carattere digitato nella partita IVA.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, active, kind, selectedSite]);

  // 3. Controllo anti-doppione.
  useEffect(() => {
    if (!isOpen || !active || !organizationId) return;
    const domain = selectedSite || extractCompanyDomain(person.email);
    const vat = companyValues.vat_number || null;
    if (!domain && !vat && !person.email) { setMatches(null); return; }

    let cancelled = false;
    (async () => {
      const found = await findExistingEntities({
        organizationId, domain, vatNumber: vat, personEmail: person.email || null,
      });
      if (!cancelled) setMatches(found);
    })();
    return () => { cancelled = true; };
  }, [isOpen, active, organizationId, selectedSite, companyValues.vat_number, person.email]);

  const participants = useMemo(() => {
    const domain = selectedSite || extractCompanyDomain(person.email);
    return collectThreadParticipants({
      messages,
      domain,
      excludeEmails: [person.email].filter(Boolean),
    });
  }, [messages, selectedSite, person.email]);

  const setCompanyField = (field: string, value: string) => {
    touchedRef.current.add(field);
    setCompanyValues(prev => ({ ...prev, [field]: value }));
  };

  const setPersonField = (field: keyof PersonValues, value: string) => {
    setPerson(prev => ({ ...prev, [field]: value }));
  };

  const toggleExtraPerson = (email: string) => {
    setExtraPeople(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  return {
    kind, setKind,
    siteCandidates, selectedSite, setSelectedSite, socialLinks,
    companyValues, companyDraft, setCompanyField,
    person, setPersonField,
    analyzing, matches,
    participants, extraPeople, toggleExtraPerson,
  };
}
